import { useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { User as UserIcon, Mail, Lock, Building2, Shield, Plus } from "lucide-react";
import { toast } from "sonner";
import { createAccessUser, type CreateAccessUserPayload } from "../../../api/accessApi";

interface CreateUserModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUserCreated: () => void;
}

export default function CreateUserModal({
  isOpen,
  onOpenChange,
  onUserCreated,
}: CreateUserModalProps) {
  const [formData, setFormData] = useState<CreateAccessUserPayload>({
    name: "",
    email: "",
    password: "",
    role: "client",
    plan: "free",
    status: "active",
    send_invite: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (onClose: () => void) => {
    if (!formData.email || !formData.name) {
      toast.error("Please fill in Name and Email address");
      return;
    }

    if (!formData.send_invite && !formData.password) {
      toast.error("Please enter a password or select Send Invite");
      return;
    }

    try {
      setIsSubmitting(true);
      await createAccessUser({
        ...formData,
        password: formData.send_invite ? undefined : formData.password,
      });
      toast.success(`User ${formData.name} created successfully`);
      onUserCreated();
      onClose();
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "client",
        plan: "free",
        status: "active",
        send_invite: false,
      });
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      backdrop="blur"
      classNames={{
        base: "bg-[#0f1320] border border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.8)] text-white",
        header: "border-b border-white/10",
        footer: "border-t border-white/10",
        closeButton: "hover:bg-white/10 text-white/60 hover:text-white",
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Plus size={18} className="text-indigo-400" /> Create User / Client
              </h2>
              <p className="text-xs text-white/50 font-normal">
                Register a new account and set their role and initial platform permissions.
              </p>
            </ModalHeader>

            <ModalBody className="py-6 space-y-4">
              {/* Full Name */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                  Full Name *
                </label>
                <div className="relative">
                  <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="text"
                    placeholder="e.g. Sarah Connor"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-900 border border-white/10 rounded-md py-2 pl-9 pr-3 text-sm text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Email Address */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="email"
                    placeholder="sarah@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-slate-900 border border-white/10 rounded-md py-2 pl-9 pr-3 text-sm text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Role & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                    className="w-full bg-slate-900 border border-white/10 rounded-md py-2 px-3 text-sm text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="client">Client</option>
                    <option value="analyst">Analyst</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full bg-slate-900 border border-white/10 rounded-md py-2 px-3 text-sm text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending Invite</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              {/* Plan Tier (Shown if role is client) */}
              {formData.role === "client" && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">Plan Tier *</label>
                  <select
                    value={formData.plan}
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                    className="w-full bg-slate-900 border border-white/10 rounded-md py-2 px-3 text-sm text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="free">Free (Operator / Basic)</option>
                    <option value="Business Pro">Business Pro</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
              )}

              {/* Password or Invite */}
              <div className="flex flex-col gap-2 pt-2 border-t border-white/10">
                <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.send_invite}
                    onChange={(e) => setFormData({ ...formData, send_invite: e.target.checked })}
                    className="accent-indigo-500 rounded"
                  />
                  Send invitation email with temporary password link
                </label>

                {!formData.send_invite && (
                  <div className="flex flex-col gap-1 mt-1">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wider">Password *</label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full bg-slate-900 border border-white/10 rounded-md py-2 pl-9 pr-3 text-sm text-white focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </ModalBody>

            <ModalFooter>
              <button
                className="px-4 py-2 text-sm text-white/60 hover:text-white rounded-md transition"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white rounded-md shadow-md transition disabled:opacity-50"
                onClick={() => handleSubmit(onClose)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating…" : "Create Account"}
              </button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
